import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, UserCheck } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { useTranslation } from "react-i18next";

interface UserDetailsFormData {
  fullName: string;
  email: string;
}

interface UserDetailsDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function UserDetailsDialog({ open, onOpenChange }: UserDetailsDialogProps) {
  const { completeProfile } = useUser();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<UserDetailsFormData>();

  const onSubmit = async (data: UserDetailsFormData) => {
    setError("");
    setLoading(true);

    try {
      await completeProfile({
        fullName: data.fullName.trim(),
        email: data.email.trim()
      });
      
      reset();
      onOpenChange?.(false);
    } catch (error: any) {
      console.error('Error completing profile:', error);
      setError(error.message || 'Failed to complete profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 bg-primary rounded-full flex items-center justify-center">
            <UserCheck className="h-6 w-6 text-primary-foreground" />
          </div>
          <DialogTitle className="text-xl font-semibold">
            {t('auth.welcomeTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('auth.welcomeSubtitle')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="fullName">{t('auth.fullName')}</Label>
            <Input
              id="fullName"
              type="text"
              placeholder="John Doe"
              {...register("fullName", {
                required: t('auth.nameRequired'),
                minLength: {
                  value: 2,
                  message: "Name must be at least 2 characters"
                }
              })}
              className={errors.fullName ? "border-destructive" : ""}
            />
            {errors.fullName && (
              <p className="text-sm text-destructive">{errors.fullName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              placeholder="john@example.com"
              {...register("email", {
                required: t('auth.emailRequired'),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('auth.invalidEmail')
                }
              })}
              className={errors.email ? "border-destructive" : ""}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              t('auth.completeProfile')
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
} 